import React, { useMemo, useState, useEffect } from "react";
import { X, Search, Filter as FilterIcon, RotateCcw, ChevronRight, Bookmark, Camera } from "lucide-react";
import { resolveDisplayValue, toNumberOrNull, applyFilters, sortUnits, fieldLabel, isEmptyFilter } from "@/lib/unitFinder";
import { L, tr } from "@/lib/i18n";

/**
 * UnitFinderDrawer — Smart Unit Finder Phase SF-1.
 *
 * A sales-assistant overlay opened from the "Find Unit" pill in the public
 * viewer. Filters are generated dynamically from the project's
 * FieldDefinitions (filterable=true) and drive a smart result list.
 */
export default function UnitFinderDrawer({
  structure, inventory, onClose, onPickUnit, onLocate, onTrack,
  lang, defaultLang, unitsWithPanorama, shortlistCodes, onToggleShortlist,
  state = {}, onStateChange,
}) {
  const project = structure.project;
  const fieldDefs = useMemo(
    () => (structure.field_definitions || []).filter((f) => f.filterable && f.public_visible !== false).sort((a, b) => (a.filter_order || 0) - (b.filter_order || 0)),
    [structure.field_definitions],
  );
  const cardFields = useMemo(
    () => (structure.field_definitions || []).filter((f) => f.card_visible && f.public_visible !== false).sort((a, b) => (a.filter_order || 0) - (b.filter_order || 0)),
    [structure.field_definitions],
  );
  // SF-3 — Saved filter presets (only visible ones). Rendered as chips above
  // the filter panel; clicking one atomically replaces the active filter set.
  const presets = useMemo(
    () => (structure.filter_presets || []).filter((p) => p.visibility !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [structure.filter_presets],
  );
  const [localState, setLocalState] = useState(state);
  const currentState = onStateChange ? state : localState;
  const updateState = (patch) => { const next = { ...currentState, ...patch }; onStateChange ? onStateChange(next) : setLocalState(next); };
  const activePresetId = currentState.activePresetId || null;

  // Visible base units (respect hidden + sold-visibility project setting).
  const baseUnits = useMemo(() => {
    let arr = inventory.entities.filter((e) => e.visibility !== false);
    if (project.viewer_show_sold === false) arr = arr.filter((e) => e.status !== "sold");
    return arr;
  }, [inventory.entities, project.viewer_show_sold]);

  const filters = currentState.filters || {};
  const sortKey = currentState.sortKey || "recommended";
  const setSortKey = (sortKey) => updateState({ sortKey });

  const results = useMemo(() => {
    const filtered = applyFilters(baseUnits, fieldDefs, filters);
    return sortUnits(filtered, structure.field_definitions, sortKey);
  }, [baseUnits, fieldDefs, structure.field_definitions, filters, sortKey]);

  useEffect(() => { onTrack?.("unit_filter_open", { project_id: project.id, source: "topbar" }); }, []);

  const setFilterFor = (key, value) => {
    const next = { ...filters };
    if (isEmptyFilter(value)) delete next[key]; else next[key] = value;
    updateState({ filters: next, activePresetId: null });
    onTrack?.("unit_filter_apply", { key, filters_active: Object.keys(next).length });
  };
  const clearAll = () => {
    updateState({ filters: {}, activePresetId: null });
    onTrack?.("unit_filter_clear", { project_id: project.id });
  };
  const applyPreset = (preset) => {
    const active = activePresetId !== preset.id;
    updateState({ filters: active ? { ...(preset.filters || {}) } : {}, activePresetId: active ? preset.id : null });
    onTrack?.("unit_filter_preset_click", { preset_id: preset.id, action: active ? "on" : "off" });
  };

  // Active filter chips
  const activeChips = useMemo(() => {
    const out = [];
    for (const [key, val] of Object.entries(filters)) {
      const fd = fieldDefs.find((f) => f.key === key);
      if (!fd) continue;
      let label = "";
      if (fd.filter_ui === "range") label = `${fieldLabel(fd, lang, defaultLang)}: ${val.min ?? "*"}–${val.max ?? "*"}${fd.unit ? " " + fd.unit : ""}`;
      else if (fd.filter_ui === "multi_select") label = `${fieldLabel(fd, lang, defaultLang)}: ${val.map(v => fd.key === "status" ? L(lang, v) : v).join(", ")}`;
      else if (fd.filter_ui === "single_select") label = `${fieldLabel(fd, lang, defaultLang)}: ${val}`;
      else if (fd.filter_ui === "toggle") label = `${fieldLabel(fd, lang, defaultLang)}: ${L(lang, val ? "yes" : "no")}`;
      else if (fd.filter_ui === "date_range") label = `${fieldLabel(fd, lang, defaultLang)}: ${val.from || "…"} – ${val.to || "…"}`;
      else if (fd.filter_ui === "search") label = `${fieldLabel(fd, lang, defaultLang)} ~ "${val}"`;
      out.push({ key, label });
    }
    return out;
  }, [filters, fieldDefs, lang, defaultLang]);

  return (
    <div className="absolute inset-0 z-40 bg-zinc-950/95 backdrop-blur-md text-white fade-in flex flex-col" data-testid="unit-finder-drawer">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/10">
        <FilterIcon className="h-4 w-4 text-amber-300" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "find_unit")}</div>
          <div className="font-display text-xl">{tr(project, "name", lang, defaultLang) || project.name}</div>
        </div>
        <div className="text-xs text-white/60">
          <span data-testid="unit-finder-count" className="text-white text-base font-medium">{L(lang, "matches_count", results.length)}</span>
        </div>
        {activeChips.length > 0 && (
          <button onClick={clearAll} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200" data-testid="unit-finder-reset">
            <RotateCcw className="h-3.5 w-3.5" /> {L(lang, "reset")}
          </button>
        )}
        <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="unit-finder-close" aria-label={L(lang, "close")}>
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body — split panel on landscape */}
      <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-[minmax(120px,35%)_1fr] lg:grid-rows-1 lg:grid-cols-[340px_1fr] overflow-hidden">
        {/* Filter panel */}
        <div className="border-r border-white/10 overflow-y-auto p-5 space-y-5" data-testid="unit-finder-filters">
          {presets.length > 0 && (
            <div data-testid="unit-finder-presets">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{L(lang, "saved_searches")}</div>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p) => {
                  const on = activePresetId === p.id;
                  const label = (p.translations && p.translations[lang]) || (p.translations && p.translations[defaultLang]) || p.name;
                  return (
                    <button key={p.id} onClick={() => applyPreset(p)}
                      className={`px-2.5 py-1.5 rounded-full text-xs border transition ${on ? "bg-amber-400/20 border-amber-400/60 text-amber-100" : "bg-white/5 border-white/15 hover:bg-white/10"}`}
                      data-testid={`unit-finder-preset-${p.id}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {fieldDefs.length === 0 && (
            <div className="text-sm text-white/55">{L(lang, "no_filters")}</div>
          )}
          {fieldDefs.map((fd) => (
            <FieldFilter key={fd.key} fd={fd} value={filters[fd.key]} onChange={(v) => setFilterFor(fd.key, v)} lang={lang} defaultLang={defaultLang} />
          ))}
          {/* Sort */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{L(lang, "sort")}</div>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="unit-finder-sort">
              <option value="recommended">{L(lang, "recommended")}</option>
              <option value="price-asc">{L(lang, "sort_price_asc")}</option>
              <option value="price-desc">{L(lang, "sort_price_desc")}</option>
              <option value="area-asc">{L(lang, "sort_area_asc")}</option>
              <option value="area-desc">{L(lang, "sort_area_desc")}</option>
              <option value="floor-asc">{L(lang, "sort_floor_asc")}</option>
              <option value="floor-desc">{L(lang, "sort_floor_desc")}</option>
              <option value="code-asc">{L(lang, "sort_code")}</option>
              <option value="status-asc">{L(lang, "sort_status")}</option>
            </select>
          </div>
        </div>

        {/* Results panel */}
        <div className="overflow-y-auto p-5">
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="unit-finder-chips">
              {activeChips.map((c) => (
                <button key={c.key} onClick={() => setFilterFor(c.key, null)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] bg-amber-500/15 border border-amber-400/40 text-amber-200 rounded-full hover:bg-amber-500/25"
                  data-testid={`unit-finder-chip-${c.key}`}>
                  {c.label} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}
          {results.length === 0 ? (
            <div className="text-center text-white/55 py-16" data-testid="unit-finder-empty">
              {L(lang, "no_matches")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {results.map((u) => (
                <ResultCard key={u.id} unit={u} project={project}
                  cardFields={cardFields}
                  shortlisted={shortlistCodes?.includes(u.code)}
                  has360={unitsWithPanorama?.has(u.id)}
                  onView={() => { onTrack?.("unit_result_open", { entity_id: u.id }); onPickUnit?.(u.id); }}
                  onLocate={() => { onLocate?.(u); }}
                  onShortlist={() => onToggleShortlist?.(u.code)}
                  lang={lang} defaultLang={defaultLang} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Per-field filter control ─────────────────────────────────────────────
function FieldFilter({ fd, value, onChange, lang, defaultLang }) {
  const label = fieldLabel(fd, lang, defaultLang);
  const ttl = fd.unit ? `${label} (${fd.unit})` : label;

  if (fd.filter_ui === "date_range") {
    return <div data-testid={`filter-${fd.key}`}><div className="text-xs mb-2">{ttl}</div><div className="grid grid-cols-2 gap-2">
      {["from", "to"].map(bound => <label key={bound} className="text-xs">{L(lang, bound)}<input type="date" aria-label={`${ttl} ${L(lang, bound)}`} value={value?.[bound] || ""} onChange={e => onChange({ ...value, [bound]: e.target.value || null })} className="w-full bg-zinc-900 rounded p-2" data-testid={`filter-${fd.key}-${bound}`} /></label>)}
    </div></div>;
  }
  if (fd.filter_ui === "range") {
    const min = value?.min ?? "";
    const max = value?.max ?? "";
    const fmtPlaceholder = (n) => n == null ? "" : (fd.data_type === "currency" ? Math.round(n).toLocaleString() : n);
    return (
      <div data-testid={`filter-${fd.key}`}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{ttl}</div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder={`${L(lang, "min")} ${fmtPlaceholder(fd.min_value)}`} value={min}
            onChange={(e) => onChange({ ...(value || {}), min: e.target.value === "" ? null : Number(e.target.value) })}
            className="bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-sm w-full"
            data-testid={`filter-${fd.key}-min`} />
          <input type="number" placeholder={`${L(lang, "max")} ${fmtPlaceholder(fd.max_value)}`} value={max}
            onChange={(e) => onChange({ ...(value || {}), max: e.target.value === "" ? null : Number(e.target.value) })}
            className="bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-sm w-full"
            data-testid={`filter-${fd.key}-max`} />
        </div>
      </div>
    );
  }
  if (fd.filter_ui === "multi_select") {
    const arr = value || [];
    const opts = fd.options && fd.options.length > 0 ? fd.options : [];
    if (opts.length === 0) return null;
    const toggle = (o) => {
      const has = arr.includes(o);
      onChange(has ? arr.filter((x) => x !== o) : [...arr, o]);
    };
    return (
      <div data-testid={`filter-${fd.key}`}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{ttl}</div>
        <div className="flex flex-wrap gap-1.5">
          {opts.map((o) => {
            const on = arr.includes(o);
            return (
              <button key={o} onClick={() => toggle(o)}
                className={`px-2.5 py-1 rounded-full text-xs border transition ${on ? "bg-white text-zinc-900 border-white" : "bg-white/5 border-white/15 hover:bg-white/10"}`}
                data-testid={`filter-${fd.key}-opt-${o}`}>
                {fd.key === "status" ? L(lang, o) : o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (fd.filter_ui === "single_select") {
    const opts = fd.options && fd.options.length > 0 ? fd.options : [];
    if (opts.length === 0) return null;
    return (
      <div data-testid={`filter-${fd.key}`}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{ttl}</div>
        <select value={value || ""} onChange={(e) => onChange(e.target.value || null)}
          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
          <option value="">{L(lang, "any")}</option>
          {opts.map((o) => <option key={o} value={o}>{fd.key === "status" ? L(lang, o) : o}</option>)}
        </select>
      </div>
    );
  }
  if (fd.filter_ui === "toggle") {
    return (
      <div data-testid={`filter-${fd.key}`} className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/55">{ttl}</span>
        <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-xs">
          <button onClick={() => onChange(null)}  className={`px-3 py-1 ${value == null ? "bg-white text-zinc-900" : "text-white/70"}`}>{L(lang, "any")}</button>
          <button onClick={() => onChange(true)}  className={`px-3 py-1 ${value === true ? "bg-white text-zinc-900" : "text-white/70"}`}>{L(lang, "yes")}</button>
          <button onClick={() => onChange(false)} className={`px-3 py-1 ${value === false ? "bg-white text-zinc-900" : "text-white/70"}`}>{L(lang, "no")}</button>
        </div>
      </div>
    );
  }
  if (fd.filter_ui === "search") {
    return (
      <div data-testid={`filter-${fd.key}`}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{ttl}</div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-white/40" />
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
            placeholder={L(lang, "search")}
            className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-1.5 text-sm" />
        </div>
      </div>
    );
  }
  return null;
}

// ── Smart result card ────────────────────────────────────────────────────
function ResultCard({ unit, project, cardFields, shortlisted, has360, onView, onLocate, onShortlist, lang, defaultLang }) {
  const statusStyle = {
    available: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
    reserved:  "bg-amber-400/15 text-amber-200 border-amber-400/30",
    sold:      "bg-red-400/15 text-red-200 border-red-400/30",
  }[unit.status] || "bg-white/10 text-white/70 border-white/15";

  // Render each card field that has a non-empty value.
  const rows = (cardFields || [])
    .map((fd) => {
      if (fd.key === "code" || fd.key === "status") return null;
      const v = resolveDisplayValue(unit, fd, lang, defaultLang);
      if (v == null || v === "") return null;
      let display = v;
      if (fd.data_type === "currency") {
        const n = toNumberOrNull(v);
        if (n != null) {
          display = new Intl.NumberFormat(lang || "en", { style: "currency", currency: fd.currency || unit.currency || project.currency || "USD", maximumFractionDigits: 0 }).format(n);
        }
      } else if (fd.unit) {
        display = `${v} ${fd.unit}`;
      }
      return { key: fd.key, label: fieldLabel(fd, lang, defaultLang), display };
    })
    .filter(Boolean);

  return (
    <div className={`rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4 ${unit.status === "sold" ? "opacity-70" : ""}`} data-testid={`unit-finder-card-${unit.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, tr(unit, "type", lang, defaultLang) || unit.type) || tr(unit, "type", lang, defaultLang) || unit.type || L(lang, "unit")}</div>
          <div className="font-display text-lg leading-tight">{unit.code || tr(unit, "name", lang, defaultLang) || L(lang, "unit")}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {has360 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-900/80 border border-white/15" title={L(lang, "tour_360")}>
              <Camera className="h-3 w-3" /> 360
            </span>
          )}
          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${statusStyle}`}>{L(lang, unit.status)}</span>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-y-1 text-xs text-white/70 mb-3">
        {rows.map((r) => (
          <React.Fragment key={r.key}>
            <dt className="text-white/40">{r.label}</dt>
            <dd className="text-right truncate">{r.display}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div className="flex items-center gap-2">
        <button onClick={onView}
          className="flex-1 px-3 py-1.5 rounded-md bg-white text-zinc-900 text-xs uppercase tracking-wider font-medium hover:bg-white/90 inline-flex items-center justify-center gap-1.5"
          data-testid={`unit-finder-view-${unit.id}`}>
          {L(lang, "open")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
        {onLocate && (
          <button onClick={onLocate}
            title={L(lang, "show_on_project")}
            className="px-3 py-1.5 rounded-md border border-white/15 text-xs uppercase tracking-wider hover:bg-white/10"
            data-testid={`unit-finder-locate-${unit.id}`}>
            {L(lang, "locate")}
          </button>
        )}
        <button onClick={onShortlist} disabled={!unit.code} title={L(lang, shortlisted ? "remove" : "add_to_shortlist")}
          className={`px-2.5 py-1.5 rounded-md border text-xs ${shortlisted ? "bg-amber-400/20 border-amber-400/50 text-amber-200" : "border-white/15 hover:bg-white/10 text-white/70"}`}
          data-testid={`unit-finder-shortlist-${unit.id}`}>
          <Bookmark className={`h-3.5 w-3.5 ${shortlisted ? "fill-current" : ""}`} />
        </button>
      </div>
    </div>
  );
}
